import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, Copy, Receipt, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ORDER_TYPE, fmtDate, fmtHours, fmtNOK } from "@/lib/format";
import { DocStatus, OrderStatus } from "@/components/drift/StatusBadge";
import { PageHeader, EmptyState, LoadingRows, KpiTile, KpiStrip, Segmented } from "@/components/drift/common";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function basisText(o) {
  const t = o.totals;
  const lines = [
    `FAKTURAGRUNNLAG ${o.ref} – ${o.title}`,
    `Kunde: ${o.customer_name}`,
    `Anlegg: ${o.location_name} – ${o.location_address || ""}`,
    `Ferdig: ${fmtDate(o.completed_at)} · Montør: ${o.assigned_name || "–"}`,
    "",
    "Timar:",
    ...o.time_entries.map((e) => `  ${e.hours} t – ${e.description || "Arbeid"} (${e.user_name || ""}, ${e.date})`),
    `  Sum timar: ${t.hours} t à ${t.timepris} kr = ${t.labour_sum} kr`,
    "",
    "Materiell:",
    ...(o.materials.length ? o.materials.map((m) => `  ${m.qty} ${m.unit} ${m.name}${m.unit_price ? ` à ${m.unit_price} kr` : ""}`) : ["  (ingen)"]),
    `  Sum materiell: ${t.material_sum} kr`,
    "",
    `Køyring: ${t.km} km à ${t.km_sats} kr = ${t.travel_sum} kr`,
    "",
    `SUM EKS. MVA: ${t.total} kr`,
    "",
    o.report ? `Arbeidsrapport: ${o.report.kunde_tekst}` : "Arbeidsrapport: MANGLAR",
  ];
  return lines.join("\n");
}

export default function InvoiceBasis() {
  const [sp, setSp] = useSearchParams();
  const status = sp.get("status") || "klar";
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get(`/drift/invoice-basis?status=${status}`).then((r) => setData(r.data)).catch(() => setData({ items: [], summary: {} })), [status]);
  useEffect(() => { setData(null); load(); }, [load]);

  const exportOne = async (o, force = false) => {
    if (force && !window.confirm("Dokumentasjonen er ikkje komplett. Eksportere likevel?")) return;
    setBusy(o.id);
    try { await api.post(`/drift/invoice-basis/${o.id}/export${force ? "?force=1" : ""}`); toast.success(`${o.ref} markert som eksportert`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const copy = async (o) => { try { await navigator.clipboard.writeText(basisText(o)); toast.success("Grunnlag kopiert"); } catch { toast.error("Kunne ikkje kopiere"); } };

  const s = data?.summary || {};
  return (
    <div data-testid="invoice-basis-page">
      <PageHeader eyebrow="Økonomi" title="Fakturagrunnlag" subtitle="Ferdige oppdrag med timar, materiell, køyring og rapport – klare for økonomisystemet." />
      {data && (
        <KpiStrip className="mb-5" cols="grid-cols-2 md:grid-cols-4">
          <KpiTile flat label={status === "klar" ? "Klare til eksport" : "Eksporterte"} value={s.count || 0} testid="invoice-kpi-count" />
          <KpiTile flat label="Timar" value={fmtHours(s.hours || 0).replace(" t", "")} hint="timar" testid="invoice-kpi-hours" />
          <KpiTile flat label="Sum eks. mva" value={fmtNOK(s.total || 0).replace(" kr", "")} hint="kr" tone="brand" testid="invoice-kpi-total" />
          <KpiTile flat label="Blokkert av dokumentasjon" value={s.blocked || 0} tone={s.blocked > 0 ? "warn" : "default"} testid="invoice-kpi-blocked" />
        </KpiStrip>
      )}
      <div className="mb-4">
        <Segmented options={[["klar", "Klare"], ["eksportert", "Eksporterte"]]} value={status} onChange={(k) => setSp(k === "klar" ? {} : { status: k })} testid="invoice-tab" />
      </div>

      {!data ? <LoadingRows n={6} header /> : data.items.length === 0 ? <EmptyState icon={Receipt} title={status === "klar" ? "Ingenting å fakturere" : "Ingen eksporterte grunnlag"} text={status === "klar" ? "Når montøren fullfører eit oppdrag, dukkar grunnlaget opp her." : ""} /> : (
        <div className="space-y-2.5">
          {data.items.map((o) => {
            const open = openId === o.id;
            const t = o.totals;
            return (
              <div key={o.id} className={cn("shadow-panel rounded-xl bg-card transition-[box-shadow] duration-200", open && "shadow-pop")} data-testid="invoice-row">
                <button onClick={() => setOpenId(open ? null : o.id)} className="grid w-full grid-cols-[1fr_auto] items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors duration-150 hover:bg-accent/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:grid-cols-[100px_1fr_110px_90px_120px_auto]" aria-expanded={open} data-testid="invoice-row-toggle">
                  <span className="hidden font-mono text-[12.5px] tabular text-muted-foreground md:block">{o.ref}</span>
                  <span className="min-w-0"><span className="block truncate text-[14px] font-medium">{o.title}</span><span className="block truncate text-[12.5px] text-muted-foreground">{o.customer_name} · {ORDER_TYPE[o.type]} · {o.assigned_name || "–"} · ferdig {fmtDate(o.completed_at, false)}</span></span>
                  <span className="hidden font-mono text-[13px] tabular md:block">{fmtHours(t.hours)}</span>
                  <span className="hidden text-xs text-muted-foreground md:block">{o.materials.length} linjer</span>
                  <span className="hidden font-mono text-[13px] tabular md:block">{fmtNOK(t.total)}</span>
                  <span className="flex items-center gap-2">{o.status === "fakturert" ? <OrderStatus status="fakturert" /> : <DocStatus status={o.report ? o.doc_check?.status : "manglar"} />}<ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} /></span>
                </button>
                {open && (
                  <div className="border-t border-border/70 px-5 py-5" data-testid="invoice-row-detail">
                    <div className="grid gap-6 lg:grid-cols-3">
                      <div>
                        <p className="text-[12.5px] font-medium text-muted-foreground">Timar</p>
                        <ul className="mt-2 divide-y divide-border text-sm">{o.time_entries.map((e) => <li key={e.id} className="flex justify-between py-1.5"><span className="text-muted-foreground">{e.description || "Arbeid"} · {e.user_name}</span><span className="font-mono tabular">{fmtHours(e.hours)}</span></li>)}</ul>
                        <p className="mt-2 flex justify-between text-sm font-medium"><span>{fmtHours(t.hours)} à {fmtNOK(t.timepris)}</span><span className="font-mono tabular">{fmtNOK(t.labour_sum)}</span></p>
                      </div>
                      <div>
                        <p className="text-[12.5px] font-medium text-muted-foreground">Materiell og køyring</p>
                        <ul className="mt-2 divide-y divide-border text-sm">{o.materials.map((m) => <li key={m.id} className="flex justify-between py-1.5"><span className="text-muted-foreground">{m.qty} {m.unit} {m.name}</span><span className="font-mono tabular">{fmtNOK(m.qty * m.unit_price)}</span></li>)}<li className="flex justify-between py-1.5"><span className="text-muted-foreground">{t.km} km à {t.km_sats} kr</span><span className="font-mono tabular">{fmtNOK(t.travel_sum)}</span></li></ul>
                        <p className="mt-2 flex justify-between text-sm font-medium"><span>Sum eks. mva</span><span className="font-mono tabular">{fmtNOK(t.total)}</span></p>
                      </div>
                      <div>
                        <p className="text-[12.5px] font-medium text-muted-foreground">Rapport og dokumentasjon</p>
                        {o.report ? <p className="mt-2 text-sm leading-relaxed">{o.report.kunde_tekst}</p> : <p className="mt-2 flex items-center gap-2 text-sm text-warn"><AlertTriangle className="h-4 w-4" /> Arbeidsrapport manglar</p>}
                        {o.doc_check?.manglar?.length > 0 && <ul className="mt-2 space-y-1 text-xs text-warn">{o.doc_check.manglar.map((m, i) => <li key={i}>• {m}</li>)}</ul>}
                        <p className="mt-2 text-xs text-muted-foreground">{o.photo_count} bilete · {o.documents?.length || 0} dokument</p>
                      </div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2 border-t border-border/70 pt-4">
                      <Button variant="outline" size="sm" asChild><Link to={`/drift/oppdrag/${o.id}`} data-testid="invoice-open-order-link">Opne oppdrag</Link></Button>
                      <Button variant="outline" size="sm" onClick={() => copy(o)} data-testid="invoice-copy-button"><Copy className="mr-1.5 h-3.5 w-3.5" /> Kopier grunnlag</Button>
                      {o.status === "ferdig" && (o.missing_docs
                        ? <Button size="sm" variant="outline" className="border-[hsl(var(--tone-warn-ring))] text-warn hover:text-warn" onClick={() => exportOne(o, true)} disabled={busy === o.id} data-testid="invoice-export-force-button"><AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Eksporter likevel</Button>
                        : <Button size="sm" className="bg-brand text-brand-foreground hover:bg-brand-dark" onClick={() => exportOne(o)} disabled={busy === o.id} data-testid="invoice-export-button"><Check className="mr-1.5 h-3.5 w-3.5" /> Marker som eksportert</Button>)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
